/**
 * BinaryParserPropertyPanel Component
 *
 * Custom property panel for binary_parser workflow nodes.
 * Provides UI for selecting saved formats, defining inline formats,
 * and configuring parsing options.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertCircle,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  FileCode,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ERROR, WARNING, FOREST_GREEN } from '@/styles/tokens';
import { useBinaryFormatStore } from '@/stores/binary-formats/binary-format-store';
import { binaryFormatApi } from '@/services/api/binaryFormatApi';
import {
  DATA_TYPE_INFO,
  dataTypeRequiresLength,
  dataTypeIsMultiByte,
  generateFieldId,
} from '@/types/binaryFormat';
import type {
  BinaryFormat,
  BinaryField,
  InlineBinaryFormat,
  DataType,
  Endianness,
  InputFormat,
} from '@/types/binaryFormat';
import * as errorUtils from '@/utils/errors';

// ================================
// Types
// ================================

export interface BinaryParserConfig {
  formatMode: 'saved' | 'inline';
  formatId?: string;
  inlineFormat?: InlineBinaryFormat;
  sourceVariable: string;
  outputMode: 'variables' | 'object' | 'both';
  variablePrefix?: string;
  outputVariable?: string;
  onError: 'fail' | 'skip' | 'default';
}

interface BinaryParserPropertyPanelProps {
  node: Node;
  nodes: Node[];
  edges: Edge[];
  onChange: (config: Partial<BinaryParserConfig>) => void;
  className?: string;
}

// ================================
// Main Component
// ================================

export const BinaryParserPropertyPanel: React.FC<BinaryParserPropertyPanelProps> = ({
  node,
  nodes,
  edges,
  onChange,
  className,
}) => {
  // State
  const [savedFormats, setSavedFormats] = useState<BinaryFormat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<BinaryFormat | null>(null);
  const [showFieldsPreview, setShowFieldsPreview] = useState(false);

  // Test parse state
  const [testData, setTestData] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [showTestSection, setShowTestSection] = useState(false);

  // Extract config from node data
  const config = (node.data?.config || {}) as BinaryParserConfig;

  // Fetch saved formats on mount
  useEffect(() => {
    fetchFormats();
  }, []);

  // Fetch selected format details when formatId changes
  useEffect(() => {
    if (config.formatMode === 'saved' && config.formatId) {
      fetchFormatDetails(config.formatId);
    } else {
      setSelectedFormat(null);
    }
  }, [config.formatId, config.formatMode]);

  const fetchFormats = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the store's fetch or direct API call
      const result = await binaryFormatApi.listUserFormats(
        // We need the user ID from auth store
        '', // Will be filled by API client from auth store
        { status: ['active', 'draft'] }
      );
      setSavedFormats(result.items);
    } catch (err: unknown) {
      console.error('Failed to fetch binary formats:', err);
      setError('Failed to load saved formats');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormatDetails = async (formatId: string) => {
    try {
      const format = await binaryFormatApi.getFormat('', formatId);
      setSelectedFormat(format);
    } catch (err) {
      console.error('Failed to fetch format details:', err);
      setSelectedFormat(null);
    }
  };

  const handleFormatModeChange = useCallback(
    (mode: 'saved' | 'inline') => {
      const updates: Partial<BinaryParserConfig> = { formatMode: mode };

      if (mode === 'inline' && !config.inlineFormat) {
        // Initialize inline format with defaults
        updates.inlineFormat = {
          inputFormat: 'base64',
          defaultEndian: 'BE',
          validationMode: 'lenient',
          fields: [],
        };
      }

      if (mode === 'saved') {
        // Clear inline format when switching to saved
        updates.inlineFormat = undefined;
      }

      onChange(updates);
    },
    [config.inlineFormat, onChange]
  );

  const handleFormatIdChange = useCallback(
    (formatId: string) => {
      onChange({ formatId: formatId || undefined });
    },
    [onChange]
  );

  const handleSourceVariableChange = useCallback(
    (value: string) => {
      onChange({ sourceVariable: value });
    },
    [onChange]
  );

  const handleOutputModeChange = useCallback(
    (mode: 'variables' | 'object' | 'both') => {
      onChange({ outputMode: mode });
    },
    [onChange]
  );

  const handleVariablePrefixChange = useCallback(
    (value: string) => {
      onChange({ variablePrefix: value || undefined });
    },
    [onChange]
  );

  const handleOutputVariableChange = useCallback(
    (value: string) => {
      onChange({ outputVariable: value || undefined });
    },
    [onChange]
  );

  const handleOnErrorChange = useCallback(
    (value: 'fail' | 'skip' | 'default') => {
      onChange({ onError: value });
    },
    [onChange]
  );

  // Test parse handler
  const handleTestParse = useCallback(async () => {
    if (!testData.trim()) {
      setTestError('Please enter test data');
      return;
    }

    // Determine which format to use
    let formatToTest: InlineBinaryFormat | null = null;

    if (config.formatMode === 'saved' && selectedFormat) {
      formatToTest = {
        inputFormat: selectedFormat.inputFormat,
        defaultEndian: selectedFormat.defaultEndian,
        fields: selectedFormat.fields,
      };
    } else if (config.formatMode === 'inline' && config.inlineFormat) {
      formatToTest = config.inlineFormat;
    }

    if (!formatToTest) {
      setTestError('No format configured. Select a saved format or define fields inline.');
      return;
    }

    if (!formatToTest.fields || formatToTest.fields.length === 0) {
      setTestError('No fields defined in the format');
      return;
    }

    setTestLoading(true);
    setTestError(null);
    setTestResult(null);

    try {
      const result = await binaryFormatApi.previewParse(
        {
          inputFormat: formatToTest.inputFormat,
          defaultEndian: formatToTest.defaultEndian,
          validationMode: 'lenient',
          fields: formatToTest.fields,
        },
        testData.trim()
      );
      setTestResult(result);
    } catch (err: unknown) {
      console.error('Test parse failed:', err);
      setTestError(errorUtils.getErrorMessageOrFallback(err, 'Failed to parse test data'));
    } finally {
      setTestLoading(false);
    }
  }, [testData, config.formatMode, config.inlineFormat, selectedFormat]);

  const handleInlineFormatChange = useCallback(
    (updates: Partial<InlineBinaryFormat>) => {
      onChange({
        inlineFormat: {
          ...config.inlineFormat,
          inputFormat: config.inlineFormat?.inputFormat || 'base64',
          defaultEndian: config.inlineFormat?.defaultEndian || 'BE',
          fields: config.inlineFormat?.fields || [],
          ...updates,
        },
      });
    },
    [config.inlineFormat, onChange]
  );

  const handleAddField = useCallback(() => {
    const currentFields = config.inlineFormat?.fields || [];
    const newField: BinaryField = {
      id: generateFieldId(),
      name: `field_${currentFields.length}`,
      offset: currentFields.length > 0
        ? Math.max(...currentFields.map(f => f.offset)) + 1
        : 0,
      type: 'uint8',
    };
    handleInlineFormatChange({ fields: [...currentFields, newField] });
  }, [config.inlineFormat?.fields, handleInlineFormatChange]);

  const handleUpdateField = useCallback(
    (fieldId: string, updates: Partial<BinaryField>) => {
      const currentFields = config.inlineFormat?.fields || [];
      const updatedFields = currentFields.map((f) =>
        f.id === fieldId ? { ...f, ...updates } : f
      );
      handleInlineFormatChange({ fields: updatedFields });
    },
    [config.inlineFormat?.fields, handleInlineFormatChange]
  );

  const handleDeleteField = useCallback(
    (fieldId: string) => {
      const currentFields = config.inlineFormat?.fields || [];
      handleInlineFormatChange({
        fields: currentFields.filter((f) => f.id !== fieldId),
      });
    },
    [config.inlineFormat?.fields, handleInlineFormatChange]
  );

  const showVariablePrefix = config.outputMode === 'variables' || config.outputMode === 'both';
  const showOutputVariable = config.outputMode === 'object' || config.outputMode === 'both';

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold mb-1">Binary Parser Configuration</h3>
        <p className="text-xs text-muted-foreground">
          Parse binary data into structured variables
        </p>
      </div>

      {/* Format Mode Tabs */}
      <Tabs
        value={config.formatMode || 'saved'}
        onValueChange={(v) => handleFormatModeChange(v as 'saved' | 'inline')}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="saved">Use Saved Format</TabsTrigger>
          <TabsTrigger value="inline">Define Inline</TabsTrigger>
        </TabsList>

        {/* Saved Format Selection */}
        <TabsContent value="saved" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="formatId" className="text-sm font-medium">
              Binary Format
              <span className="text-red-500 ml-1">*</span>
            </Label>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading formats...
              </div>
            ) : error ? (
              <div
                className="rounded p-3"
                style={{ backgroundColor: ERROR.bg, border: `1px solid ${ERROR.border}` }}
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" style={{ color: ERROR.icon }} />
                  <span className="text-xs" style={{ color: ERROR.text }}>{error}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={fetchFormats}
                    className="h-6 px-2 text-xs ml-auto"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                </div>
              </div>
            ) : (
              <Select
                value={config.formatId || ''}
                onValueChange={handleFormatIdChange}
              >
                <SelectTrigger id="formatId" className="w-full">
                  <SelectValue placeholder="Select a format..." />
                </SelectTrigger>
                <SelectContent>
                  {savedFormats.length === 0 ? (
                    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                      <FileCode className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No binary formats found</p>
                      <p className="text-xs mt-1">
                        Create one at /binary-formats
                      </p>
                    </div>
                  ) : (
                    savedFormats.map((format) => (
                      <SelectItem key={format._id} value={format._id}>
                        <div className="flex items-center gap-2">
                          <span>{format.name}</span>
                          <Badge
                            variant={format.status === 'active' ? 'default' : 'secondary'}
                            className="text-[10px] px-1 py-0"
                          >
                            {format.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            ({format.fields.length} fields)
                          </span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Selected Format Preview */}
          {selectedFormat && (
            <Collapsible open={showFieldsPreview} onOpenChange={setShowFieldsPreview}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-start">
                  {showFieldsPreview ? (
                    <ChevronDown className="h-4 w-4 mr-2" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mr-2" />
                  )}
                  Preview Format ({selectedFormat.fields.length} fields)
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border rounded-md p-3 mt-2 space-y-2 bg-muted/30">
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Input:</span>
                      <Badge variant="outline" className="text-xs">
                        {selectedFormat.inputFormat.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Endianness:</span>
                      <span>{selectedFormat.defaultEndian}</span>
                    </div>
                  </div>
                  <div className="border-t pt-2">
                    <p className="text-xs font-medium mb-1">Fields:</p>
                    <ScrollArea className="h-32">
                      <div className="space-y-1">
                        {selectedFormat.fields.map((field) => (
                          <div
                            key={field.id}
                            className="flex items-center justify-between text-xs py-1 px-2 bg-background rounded"
                          >
                            <span className="font-medium">{field.name}</span>
                            <span className="text-muted-foreground">
                              @{field.offset} · {field.type}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </TabsContent>

        {/* Inline Format Definition */}
        <TabsContent value="inline" className="space-y-4 mt-4">
          {/* Inline Format Settings */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Input Format</Label>
              <Select
                value={config.inlineFormat?.inputFormat || 'base64'}
                onValueChange={(v) =>
                  handleInlineFormatChange({ inputFormat: v as InputFormat })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="base64">Base64</SelectItem>
                  <SelectItem value="hex">Hex</SelectItem>
                  <SelectItem value="binary">Binary</SelectItem>
                  <SelectItem value="raw">Raw</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Byte Order</Label>
              <Select
                value={config.inlineFormat?.defaultEndian || 'BE'}
                onValueChange={(v) =>
                  handleInlineFormatChange({ defaultEndian: v as Endianness })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BE">Big Endian</SelectItem>
                  <SelectItem value="LE">Little Endian</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Inline Fields */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">
                Fields ({config.inlineFormat?.fields?.length || 0})
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={handleAddField}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </div>

            {(!config.inlineFormat?.fields || config.inlineFormat.fields.length === 0) ? (
              <div className="text-center py-4 text-muted-foreground text-xs border rounded-md">
                No fields defined. Click "Add" to create a field.
              </div>
            ) : (
              <ScrollArea className="h-48">
                <div className="space-y-2 pr-2">
                  {config.inlineFormat.fields.map((field) => (
                    <InlineFieldEditor
                      key={field.id}
                      field={field}
                      defaultEndian={config.inlineFormat?.defaultEndian || 'BE'}
                      onUpdate={(updates) => handleUpdateField(field.id, updates)}
                      onDelete={() => handleDeleteField(field.id)}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Divider */}
      <div className="border-t my-4" />

      {/* Source Variable */}
      <div className="space-y-2">
        <Label htmlFor="sourceVariable" className="text-sm font-medium">
          Source Variable
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Input
          id="sourceVariable"
          value={config.sourceVariable || ''}
          onChange={(e) => handleSourceVariableChange(e.target.value)}
          placeholder="e.g., webhookPayload.data or payload"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Path to the binary data in workflow variables
        </p>
      </div>

      {/* Output Mode */}
      <div className="space-y-2">
        <Label htmlFor="outputMode" className="text-sm font-medium">
          Output Mode
        </Label>
        <Select
          value={config.outputMode || 'variables'}
          onValueChange={handleOutputModeChange}
        >
          <SelectTrigger id="outputMode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="variables">
              <div>
                <div className="font-medium">Individual Variables</div>
                <div className="text-xs text-muted-foreground">
                  Create separate variable for each field
                </div>
              </div>
            </SelectItem>
            <SelectItem value="object">
              <div>
                <div className="font-medium">Single Object</div>
                <div className="text-xs text-muted-foreground">
                  Store all fields in one object variable
                </div>
              </div>
            </SelectItem>
            <SelectItem value="both">
              <div>
                <div className="font-medium">Both</div>
                <div className="text-xs text-muted-foreground">
                  Create both individual variables and object
                </div>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Variable Prefix (for variables mode) */}
      {showVariablePrefix && (
        <div className="space-y-2">
          <Label htmlFor="variablePrefix" className="text-sm font-medium">
            Variable Prefix
          </Label>
          <Input
            id="variablePrefix"
            value={config.variablePrefix || ''}
            onChange={(e) => handleVariablePrefixChange(e.target.value)}
            placeholder="e.g., sensor_"
          />
          <p className="text-xs text-muted-foreground">
            Prefix added to field names (e.g., sensor_temperature)
          </p>
        </div>
      )}

      {/* Output Variable (for object mode) */}
      {showOutputVariable && (
        <div className="space-y-2">
          <Label htmlFor="outputVariable" className="text-sm font-medium">
            Output Variable Name
          </Label>
          <Input
            id="outputVariable"
            value={config.outputVariable || ''}
            onChange={(e) => handleOutputVariableChange(e.target.value)}
            placeholder="parsedData"
          />
          <p className="text-xs text-muted-foreground">
            Variable name for the parsed object
          </p>
        </div>
      )}

      {/* Error Handling */}
      <div className="space-y-2">
        <Label htmlFor="onError" className="text-sm font-medium">
          On Error
        </Label>
        <Select
          value={config.onError || 'fail'}
          onValueChange={handleOnErrorChange}
        >
          <SelectTrigger id="onError">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fail">Fail workflow</SelectItem>
            <SelectItem value="skip">Skip (continue workflow)</SelectItem>
            <SelectItem value="default">Use default values</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Test Parse Section */}
      <div className="border-t my-4" />
      <Collapsible open={showTestSection} onOpenChange={setShowTestSection}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" size="sm" className="w-full justify-between">
            <span className="flex items-center gap-2">
              <Play className="h-4 w-4" />
              Test Parse
            </span>
            {showTestSection ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-3">
          {/* Input Format Indicator */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="font-mono">
              {config.formatMode === 'saved' && selectedFormat
                ? selectedFormat.inputFormat.toUpperCase()
                : config.inlineFormat?.inputFormat?.toUpperCase() || 'BASE64'}
            </Badge>
            <span>format expected</span>
          </div>

          {/* Test Data Input */}
          <div className="space-y-2">
            <Label htmlFor="testData" className="text-xs font-medium">
              Sample Payload
            </Label>
            <Textarea
              id="testData"
              value={testData}
              onChange={(e) => setTestData(e.target.value)}
              placeholder={getTestDataPlaceholder(
                config.formatMode === 'saved' && selectedFormat
                  ? selectedFormat.inputFormat
                  : config.inlineFormat?.inputFormat || 'base64'
              )}
              className="font-mono text-xs min-h-[80px] resize-none"
            />
          </div>

          {/* Test Button */}
          <Button
            type="button"
            onClick={handleTestParse}
            disabled={testLoading || !testData.trim()}
            className="w-full"
            size="sm"
          >
            {testLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Parsing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Test Parse
              </>
            )}
          </Button>

          {/* Test Error */}
          {testError && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-xs">
              <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{testError}</span>
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div className="space-y-2">
              {/* Status Header */}
              <div className="flex items-center justify-between p-2 rounded-md bg-muted/50">
                <div className="flex items-center gap-2">
                  {testResult.result?.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                  <span className="text-sm font-medium">
                    {testResult.result?.success ? 'Success' : 'Parse Failed'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {testResult.result?.executionTimeMs?.toFixed(2) || 0}ms
                  <Badge variant="secondary" className="text-xs">
                    {testResult.byteCount || 0} bytes
                  </Badge>
                </div>
              </div>

              {/* Parsed Data */}
              {testResult.result?.data && Object.keys(testResult.result.data).length > 0 && (
                <div className="border rounded-md p-2 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Parsed Values:</p>
                  <ScrollArea className="max-h-40">
                    <div className="space-y-1">
                      {Object.entries(testResult.result.data).map(([key, value]) => (
                        <div
                          key={key}
                          className="flex items-center justify-between text-xs py-1 px-2 bg-muted/30 rounded"
                        >
                          <span className="font-medium">{key}</span>
                          <span className="font-mono text-muted-foreground">
                            {formatTestValue(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Errors */}
              {testResult.result?.errors && testResult.result.errors.length > 0 && (
                <div className="space-y-1">
                  {testResult.result.errors.map((err: any, i: number) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-destructive">
                      <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>
                        <span className="font-medium">{err.field}:</span> {err.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Hex Dump Preview */}
              {testResult.hexDump && (
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-start h-7 text-xs">
                      <ChevronRight className="h-3 w-3 mr-1" />
                      Hex Dump
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <pre className="text-[10px] font-mono bg-muted p-2 rounded overflow-x-auto max-h-20">
                      {testResult.hexDump}
                    </pre>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

// ================================
// Inline Field Editor Sub-component
// ================================

interface InlineFieldEditorProps {
  field: BinaryField;
  defaultEndian: Endianness;
  onUpdate: (updates: Partial<BinaryField>) => void;
  onDelete: () => void;
}

const InlineFieldEditor: React.FC<InlineFieldEditorProps> = ({
  field,
  defaultEndian,
  onUpdate,
  onDelete,
}) => {
  const showLength = dataTypeRequiresLength(field.type);
  const showEndian = dataTypeIsMultiByte(field.type);
  const showBitFields = field.type === 'bits';

  return (
    <div className="border rounded-md p-2 bg-background space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="font-mono text-xs shrink-0">
          @{field.offset}
        </Badge>
        <Input
          value={field.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="Field name"
          className="h-7 text-xs flex-1"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Offset</Label>
          <Input
            type="number"
            min="0"
            value={field.offset}
            onChange={(e) => onUpdate({ offset: parseInt(e.target.value) || 0 })}
            className="h-7 text-xs"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Type</Label>
          <Select
            value={field.type}
            onValueChange={(v) => onUpdate({ type: v as DataType })}
          >
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATA_TYPE_INFO.map((t) => (
                <SelectItem key={t.type} value={t.type} className="text-xs">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {showLength && (
          <div>
            <Label className="text-[10px] text-muted-foreground">Length</Label>
            <Input
              type="number"
              min="1"
              value={field.length || ''}
              onChange={(e) => onUpdate({ length: parseInt(e.target.value) || undefined })}
              className="h-7 text-xs"
              placeholder="bytes"
            />
          </div>
        )}
        {showEndian && (
          <div>
            <Label className="text-[10px] text-muted-foreground">Endian</Label>
            <Select
              value={field.endian || ''}
              onValueChange={(v) => onUpdate({ endian: v ? (v as Endianness) : undefined })}
            >
              <SelectTrigger className="h-7 text-xs">
                <SelectValue placeholder={defaultEndian} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BE" className="text-xs">BE</SelectItem>
                <SelectItem value="LE" className="text-xs">LE</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {showBitFields && (
          <>
            <div>
              <Label className="text-[10px] text-muted-foreground">Bit Off</Label>
              <Input
                type="number"
                min="0"
                max="7"
                value={field.bitOffset ?? 0}
                onChange={(e) => onUpdate({ bitOffset: parseInt(e.target.value) || 0 })}
                className="h-7 text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Bit Len</Label>
              <Input
                type="number"
                min="1"
                max="64"
                value={field.bitLength ?? 1}
                onChange={(e) => onUpdate({ bitLength: parseInt(e.target.value) || 1 })}
                className="h-7 text-xs"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ================================
// Utility Functions
// ================================

/**
 * Get placeholder text for test data input based on format
 */
function getTestDataPlaceholder(format: InputFormat): string {
  switch (format) {
    case 'base64':
      return 'Enter base64-encoded data, e.g., SGVsbG8gV29ybGQ=';
    case 'hex':
      return 'Enter hex bytes, e.g., 48 65 6C 6C 6F or 48656C6C6F';
    case 'binary':
      return 'Enter binary string, e.g., 01001000 01100101';
    case 'raw':
      return 'Enter raw bytes as string';
    default:
      return 'Enter sample data...';
  }
}

/**
 * Format a value for display in test results
 */
function formatTestValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  if (typeof value === 'number') {
    // Show both decimal and hex for small integers
    if (Number.isInteger(value) && value >= 0 && value <= 0xffff) {
      return `${value} (0x${value.toString(16).toUpperCase()})`;
    }
    // Round floats for display
    return Number.isInteger(value) ? String(value) : value.toFixed(4);
  }
  return String(value);
}

export default BinaryParserPropertyPanel;
